import { useState, useEffect } from 'react';

const FAVORITES_KEY = 'toolref-favorites';
const RESOURCE_FAVORITES_KEY = 'toolref-resource-favorites';
const JOB_BOARD_FAVORITES_KEY = 'toolref-job-board-favorites';

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      if (stored) {
        setFavorites(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  }, []);

  const saveFavorites = (newFavorites: string[]) => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      setFavorites(newFavorites);
    } catch (error) {
      console.error('Failed to save favorites:', error);
    }
  };

  const toggleFavorite = (toolId: string) => {
    const newFavorites = favorites.includes(toolId)
      ? favorites.filter(id => id !== toolId)
      : [...favorites, toolId];
    
    saveFavorites(newFavorites);
  };

  const addFavorite = (toolId: string) => {
    if (!favorites.includes(toolId)) {
      saveFavorites([...favorites, toolId]);
    }
  };

  const removeFavorite = (toolId: string) => {
    saveFavorites(favorites.filter(id => id !== toolId));
  };

  const clearFavorites = () => {
    saveFavorites([]);
  };

  return {
    favorites,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    clearFavorites,
  };
}

export function useResourceFavorites() {
  const [resourceFavorites, setResourceFavorites] = useState<string[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RESOURCE_FAVORITES_KEY);
      if (stored) {
        setResourceFavorites(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load resource favorites:', error);
    }
  }, []);

  const saveResourceFavorites = (newFavorites: string[]) => {
    try {
      localStorage.setItem(RESOURCE_FAVORITES_KEY, JSON.stringify(newFavorites));
      setResourceFavorites(newFavorites);
    } catch (error) {
      console.error('Failed to save resource favorites:', error);
    }
  };

  const toggleResourceFavorite = (resourceId: string) => {
    const newFavorites = resourceFavorites.includes(resourceId)
      ? resourceFavorites.filter(id => id !== resourceId)
      : [...resourceFavorites, resourceId];
    
    saveResourceFavorites(newFavorites);
  };

  const addResourceFavorite = (resourceId: string) => {
    if (!resourceFavorites.includes(resourceId)) {
      saveResourceFavorites([...resourceFavorites, resourceId]);
    }
  };

  const removeResourceFavorite = (resourceId: string) => {
    saveResourceFavorites(resourceFavorites.filter(id => id !== resourceId));
  };

  const clearResourceFavorites = () => {
    saveResourceFavorites([]);
  };

  return {
    resourceFavorites,
    toggleResourceFavorite,
    addResourceFavorite,
    removeResourceFavorite,
    clearResourceFavorites,
  };
}

export function useJobBoardFavorites() {
  const [jobBoardFavorites, setJobBoardFavorites] = useState<string[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(JOB_BOARD_FAVORITES_KEY);
      if (stored) {
        setJobBoardFavorites(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load job board favorites:', error);
    }
  }, []);

  const saveJobBoardFavorites = (newFavorites: string[]) => {
    try {
      localStorage.setItem(JOB_BOARD_FAVORITES_KEY, JSON.stringify(newFavorites));
      setJobBoardFavorites(newFavorites);
    } catch (error) {
      console.error('Failed to save job board favorites:', error);
    }
  };

  const toggleJobBoardFavorite = (jobBoardId: string) => {
    const newFavorites = jobBoardFavorites.includes(jobBoardId)
      ? jobBoardFavorites.filter(id => id !== jobBoardId)
      : [...jobBoardFavorites, jobBoardId];
    
    saveJobBoardFavorites(newFavorites);
  };

  const addJobBoardFavorite = (jobBoardId: string) => {
    if (!jobBoardFavorites.includes(jobBoardId)) {
      saveJobBoardFavorites([...jobBoardFavorites, jobBoardId]);
    }
  };

  const removeJobBoardFavorite = (jobBoardId: string) => {
    saveJobBoardFavorites(jobBoardFavorites.filter(id => id !== jobBoardId));
  };

  const clearJobBoardFavorites = () => {
    saveJobBoardFavorites([]);
  };

  return {
    jobBoardFavorites,
    toggleJobBoardFavorite,
    addJobBoardFavorite,
    removeJobBoardFavorite,
    clearJobBoardFavorites,
  };
}